import React, { useEffect, useState, useRef } from 'react';
import { Box, Card } from '@mui/material';

// Shared premium UI primitives — brand palette (#0055B8 / #6DABE4).
// Used to elevate the marketing pages with depth, motion and refined hover.

export const BRAND = '#0055B8';
export const BRAND_LIGHT = '#6DABE4';
export const BRAND_GRAD = 'linear-gradient(135deg,#0055B8,#6DABE4)';

// soft radial glow for atmosphere/depth
export const Glow: React.FC<{ color?: string; top?: any; bottom?: any; right?: any; left?: any; size?: number; opacity?: number }> = ({
  color = BRAND_LIGHT, top, bottom, right, left, size = 480, opacity = 0.18,
}) => (
  <Box aria-hidden sx={{
    position: 'absolute', top, bottom, right, left, width: size, height: size,
    background: `radial-gradient(circle, ${color} 0%, transparent 65%)`,
    opacity, filter: 'blur(24px)', pointerEvents: 'none', zIndex: 0,
  }} />
);

// faint dotted-grid texture (light surfaces use dark dots, dark surfaces use light dots)
export const GridTexture: React.FC<{ dark?: boolean; opacity?: number }> = ({ dark = false, opacity }) => {
  const o = opacity ?? (dark ? 0.07 : 0.05);
  const dot = dark ? `rgba(255,255,255,${o})` : `rgba(0,85,184,${o})`;
  return (
    <Box aria-hidden sx={{
      position: 'absolute', inset: 0,
      backgroundImage: `radial-gradient(${dot} 1px, transparent 1px)`,
      backgroundSize: '24px 24px',
      maskImage: 'radial-gradient(ellipse at 50% 35%, #000 20%, transparent 78%)',
      WebkitMaskImage: 'radial-gradient(ellipse at 50% 35%, #000 20%, transparent 78%)',
      pointerEvents: 'none', zIndex: 0,
    }} />
  );
};

// reveal-on-scroll (one-shot, respects reduced-motion)
export const Reveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Box ref={ref} sx={{
      opacity: seen ? 1 : 0,
      transform: seen ? 'none' : 'translateY(26px)',
      transition: 'opacity .75s cubic-bezier(.22,1,.36,1), transform .75s cubic-bezier(.22,1,.36,1)',
      transitionDelay: `${delay}s`,
    }}>{children}</Box>
  );
};

// card with cursor-following spotlight glow; no hover-lift (calm, premium)
export const SpotCard: React.FC<{ children: React.ReactNode; sx?: any; glow?: string; onClick?: () => void }> = ({ children, sx, glow = '0,85,184', onClick }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return (
    <Card ref={ref} onMouseMove={onMove} onClick={onClick} sx={{
      ...(onClick ? { cursor: 'pointer' } : {}),
      ...sx, position: 'relative', overflow: 'hidden',
      transition: 'box-shadow .25s ease',
      '&::before': {
        content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit', zIndex: 0,
        background: `radial-gradient(200px circle at var(--mx,50%) var(--my,-20%), rgba(${glow},0.10), transparent 62%)`,
        opacity: 0, transition: 'opacity .3s ease', pointerEvents: 'none',
      },
      '&:hover': { transform: 'none', boxShadow: '0 18px 42px -18px rgba(26,35,50,0.18)' },
      '&:hover::before': { opacity: 1 },
      '& > *': { position: 'relative', zIndex: 1 },
    }}>{children}</Card>
  );
};
